import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import './Factory.css';
import { fetchFactories, fetchFactoryDashboard, fetchEquipments, fetchMachineDashboard, fetchFactoryLines } from '../../services/api';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useTranslation } from '../../context/LanguageContext';
import SharedFactoryFlag, { countryOfFactory } from '../../components/factory/FactoryFlag';

/* 공장 헤더 국기. 예전엔 베트남 국기 모양(빨간 원+노란 별)이 하드코딩돼 한국 공장에도 그대로 떴다.
 * 공장 API 에 국가 컬럼이 없어 주소(address/location)와 공장명 키워드로 판별한다.
 * 이모지 국기는 Windows Chrome 에서 글자로 깨져 인라인 SVG 로 그린다. */
/* 국기 아이콘·국가 판별은 components/factory/FactoryFlag 로 옮겼다 (전사 통합 대시보드와 공유) */
const FactoryFlag = ({ country }) => <SharedFactoryFlag country={country} cls="fo-flag" />;

/* ===== 공장 사출 현황 (Figma「공장 모니터링 > 공장」새 디자인) =====
 * 구성: 헤더(브레드크럼·공장 선택·동기화 시각) / OVERVIEW(게이지·KPI·환경) /
 *       라인별 현황 / 설비별 현황(상태·라인 필터) / 실시간 알람(심각도 필터)
 *
 * 데이터 출처
 * - /dashboard/factory/{id}    : KPI, 라인별 현황, 알람 목록·요약
 * - /dashboard/machines/{id}[/{lineId}] : 설비별 카드(금형번호=craftId, 샷카운트=shotNo)
 * - /factory/{id}/lines        : 라인 필터 목록
 * - /dashboard/factories       : 공장 목록(온도·습도 포함)
 * 백엔드가 아직 내려주지 않는 값(OEE, 소음)은 '-' 로 둔다. 화면에 없는 숫자를 만들지 않는다. */

const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? '-' : new Intl.NumberFormat('ko-KR').format(Number(n)));
/* CAV·C/T 처럼 0 이하면 미측정인 값 */
const positive = (v) => v != null && v !== '' && Number.isFinite(Number(v)) && Number(v) > 0;
const pct = (n) => (n == null || Number.isNaN(Number(n)) ? '-' : Math.round(Number(n) * 10) / 10);

const pad2 = (n) => String(n).padStart(2, '0');
const formatDateTime = (d) => {
  if (!d) return '-';
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return '-';
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())} ${pad2(x.getHours())}:${pad2(x.getMinutes())}:${pad2(x.getSeconds())}`;
};

/* 설비 카드 좌상단 큰 번호. 설비명 끝의 숫자(사출기 #53 → 53)를 우선 쓰고, 없으면 순번. */
const machineNumber = (machine, index) => {
  const m = String(machine.name || machine.equipmentCode || '').match(/(\d+)\s*$/);
  const n = m ? Number(m[1]) : index + 1;
  return pad2(n);
};

const normalizeSeverity = (s) => {
  const v = (s || '').toUpperCase();
  if (v === 'CRITICAL' || v === 'ERROR') return 'critical';
  if (v === 'WARNING') return 'warning';
  return 'info';
};

/* 게이지. 디자인처럼 가로로 넓고 위아래가 살짝 눌린 타원 호로 그린다.
 * cut: 수평선에서 위로 잘라내는 각도. 양수면 끝이 수평선보다 위에서 끊겨 안으로 말리지 않는다.
 * 타원 호 길이는 닫힌 식이 없어 pathLength="100" 으로 정규화하고 dasharray 를 % 로 준다. */
const GAUGE = { cx: 100, cy: 74, rx: 88, ry: 66, cut: 10 };
function ArcGauge({ value, label }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const { cx, cy, rx, ry, cut } = GAUGE;
  const rad = (cut * Math.PI) / 180;
  const dx = rx * Math.cos(rad);
  const dy = ry * Math.sin(rad);
  const largeArc = cut < 0 ? 1 : 0; // 180° 를 넘을 때만 큰 호
  const d = `M ${cx - dx} ${cy - dy} A ${rx} ${ry} 0 ${largeArc} 1 ${cx + dx} ${cy - dy}`;
  return (
    <div className="fo-gauge">
      <svg viewBox="0 0 200 88" className="fo-gauge-svg" aria-hidden="true">
        <path d={d} className="fo-gauge-track" pathLength="100" />
        <path
          d={d}
          className="fo-gauge-fill"
          pathLength="100"
          style={{ strokeDasharray: `${v} 100` }}
        />
      </svg>
      <div className="fo-gauge-text">
        <span className="fo-gauge-label">{label}</span>
        <span className="fo-gauge-value">{value == null ? '-' : <>{Math.round(v)}<small>%</small></>}</span>
      </div>
    </div>
  );
}

function Factory() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tf = (key, params) => t(`pages.monitoring.factory.${key}`, params);
  const [searchParams] = useSearchParams();

  const [factoriesData, setFactoriesData] = useState([]);
  const [selectedFactoryId, setSelectedFactoryId] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [allEquipments, setAllEquipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [lines, setLines] = useState([]);
  const [machines, setMachines] = useState([]);
  const [machinesLoading, setMachinesLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');   // all | running | idle | stopped
  const [lineFilter, setLineFilter] = useState('all');       // all | lineId
  const [alarmFilter, setAlarmFilter] = useState('all');     // all | critical | warning

  const refreshIndicatorRef = useRef(null);
  const selectedFactoryIdRef = useRef(null);
  const lineFilterRef = useRef('all');

  /* ---- 설비 목록(알람 → 설비 화면 이동용) ---- */
  useEffect(() => {
    (async () => {
      try {
        const equipments = await fetchEquipments();
        setAllEquipments(equipments || []);
      } catch (err) {
        console.error('사출기 목록 조회 실패:', err);
      }
    })();
  }, []);

  /* ---- 드롭다운 바깥 클릭 ---- */
  useEffect(() => {
    const onDown = (e) => {
      if (isDropdownOpen && !e.target.closest('.fo-factory-select')) setIsDropdownOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isDropdownOpen]);

  /* ---- 공장 목록 ---- */
  useEffect(() => {
    // URL 에 factoryId 가 있으면 목록 응답을 기다리지 않고 바로 선택해 대시보드 조회를 함께 시작한다.
    // 목록은 드롭다운 표시용이고, 선택값 검증은 아래에서 목록이 오면 한 번 더 한다.
    const fromUrl = parseInt(searchParams.get('factoryId'), 10);
    if (fromUrl) setSelectedFactoryId(fromUrl);

    const loadFactories = async () => {
      try {
        const data = await fetchFactories();
        const factories = Array.isArray(data) ? data : [];
        setFactoriesData(factories);
        if (factories.length === 0) {
          setLoading(false);
          return;
        }
        // URL 의 factoryId 가 없거나 목록에 없는 값이면 첫 번째 공장을 기본 선택
        if (!fromUrl || !factories.some((f) => f.id === fromUrl)) {
          setSelectedFactoryId(factories[0].id);
        }
      } catch (err) {
        console.error('공장 목록 조회 실패:', err);
        setError(tf('factoryListLoadFailed'));
        setLoading(false);
      }
    };
    loadFactories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /* ---- 대시보드 + 설비 카드 로딩 ---- */
  const loadMachines = useCallback(async (factoryId, lineId) => {
    setMachinesLoading(true);
    try {
      const data = await fetchMachineDashboard(factoryId, lineId === 'all' ? null : lineId);
      setMachines(data?.machines || []);
    } catch (err) {
      console.error('설비 현황 조회 실패:', err);
      setMachines([]);
    } finally {
      setMachinesLoading(false);
    }
  }, []);

  const loadDashboard = useCallback(async (isAutoRefresh = false) => {
    const factoryId = selectedFactoryIdRef.current;
    if (!factoryId) return;
    try {
      if (isAutoRefresh) {
        if (refreshIndicatorRef.current) {
          refreshIndicatorRef.current.classList.add('refreshing');
          setTimeout(() => refreshIndicatorRef.current?.classList.remove('refreshing'), 1500);
        }
      } else {
        setLoading(true);
      }
      setError(null);
      const [dash] = await Promise.all([
        fetchFactoryDashboard(factoryId),
        loadMachines(factoryId, lineFilterRef.current)
      ]);
      setDashboardData(dash);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('대시보드 데이터 조회 실패:', err);
      if (!isAutoRefresh) setError(tf('loadFailed'));
    } finally {
      if (!isAutoRefresh) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMachines]);

  /* 공장이 바뀌면 라인 목록을 새로 받고 필터를 초기화한 뒤 대시보드를 읽는다 */
  useEffect(() => {
    if (!selectedFactoryId) return;
    selectedFactoryIdRef.current = selectedFactoryId;
    lineFilterRef.current = 'all';
    setLineFilter('all');
    setStatusFilter('all');
    (async () => {
      try {
        const data = await fetchFactoryLines(selectedFactoryId);
        setLines(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('라인 목록 조회 실패:', err);
        setLines([]);
      }
    })();
    loadDashboard(false);
  }, [selectedFactoryId, loadDashboard]);

  /* 라인 필터는 서버에서 걸러 받는다 (machines API 가 lineId 를 지원) */
  const handleLineFilter = (value) => {
    setLineFilter(value);
    lineFilterRef.current = value;
    if (selectedFactoryIdRef.current) loadMachines(selectedFactoryIdRef.current, value);
  };

  useAutoRefresh(() => loadDashboard(true), !!selectedFactoryId);

  /* ---- 파생 데이터 ---- */
  const currentFactory = factoriesData.find((f) => f.id === selectedFactoryId) || {};
  const factoryInfo = dashboardData?.factoryInfo || {};
  const kpi = dashboardData?.kpiCards || {};
  const lineStatuses = dashboardData?.lineStatuses || [];
  const alarmStatus = dashboardData?.alarmStatus || { activeCount: 0, alarmList: [] };

  const allMachinesMap = useMemo(() => {
    const map = {};
    allEquipments.forEach((eq) => {
      const id = eq.id || eq.equipmentId || eq.equipment_id;
      if (id) map[id] = { ...eq, id };
    });
    return map;
  }, [allEquipments]);

  const visibleMachines = useMemo(
    () => (statusFilter === 'all' ? machines : machines.filter((m) => m.status === statusFilter)),
    [machines, statusFilter]
  );

  const alarmCounts = useMemo(() => {
    const list = alarmStatus.alarmList || [];
    return {
      all: list.length,
      critical: list.filter((a) => normalizeSeverity(a.severity) === 'critical').length,
      warning: list.filter((a) => normalizeSeverity(a.severity) === 'warning').length
    };
  }, [alarmStatus.alarmList]);

  /* 진행 중인 위험 알람이 걸린 설비명. 설비 API 는 알람 개수만 주고 심각도는 없어서
   * 같은 대시보드 응답의 알람 목록에서 설비명으로 찾는다. */
  const criticalMachineNames = useMemo(() => {
    const set = new Set();
    (alarmStatus.alarmList || []).forEach((a) => {
      if (a.isActive !== false && normalizeSeverity(a.severity) === 'critical') {
        const name = a.machine || a.equipmentName || a.machineName;
        if (name) set.add(name);
      }
    });
    return set;
  }, [alarmStatus.alarmList]);

  const visibleAlarms = useMemo(() => {
    const list = alarmStatus.alarmList || [];
    if (alarmFilter === 'all') return list;
    return list.filter((a) => normalizeSeverity(a.severity) === alarmFilter);
  }, [alarmStatus.alarmList, alarmFilter]);

  const syncTime = dashboardData?.lastSyncTime ? new Date(dashboardData.lastSyncTime) : lastUpdated;

  const goMachine = (machine, openTab) => {
    navigate('/machine', { state: openTab ? { machineData: machine, openTab } : { machineData: machine } });
  };

  /* ---- 로딩 / 빈 상태 / 에러 ---- */
  // 첫 로딩: 페이지 전체를 막지 않고 헤더는 바로 그린다. 본문만 아래 return 에서 로딩 표시로 분기.
  const isInitialLoading = loading && !dashboardData;
  if (!loading && factoriesData.length === 0) {
    return (
      <MainLayout>
        <div className="fo-page">
          <div className="fo-message">
            <div>{tf('noFactories')}</div>
            <div className="fo-message-sub">{tf('registerFactoryHint')}</div>
          </div>
        </div>
      </MainLayout>
    );
  }
  if (error) {
    return (
      <MainLayout>
        <div className="fo-page">
          <div className="fo-message fo-message-error">
            <div>{error}</div>
            <button type="button" className="fo-retry" onClick={() => loadDashboard(false)}>{tf('retry')}</button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const factoryName = factoryInfo.factoryName || currentFactory.factoryName
    || (isInitialLoading ? tf('loadingData') : tf('selectFactory'));
  const factoryCode = factoryInfo.factoryCode || currentFactory.factoryCode;
  const factoryCountry = countryOfFactory(factoryName, factoryInfo.address || currentFactory.location);

  return (
    <MainLayout>
      <div className="fo-page">
        {/* ===== 헤더: 브레드크럼 + 공장 선택 + 동기화 시각 ===== */}
        <div className="fo-header">
          <div className="fo-breadcrumb">
            <span className="material-symbols-outlined fo-breadcrumb-icon">factory</span>
            <span className="fo-breadcrumb-root">{tf('breadcrumbRoot')}</span>
            <span className="material-symbols-outlined fo-breadcrumb-sep">chevron_right</span>
            <div className="fo-factory-select">
              <button type="button" className="fo-factory-trigger" onClick={() => setIsDropdownOpen((v) => !v)}>
                <span>{factoryName}</span>
                <span className="material-symbols-outlined">{isDropdownOpen ? 'expand_less' : 'expand_more'}</span>
              </button>
              {isDropdownOpen && (
                <div className="fo-factory-menu">
                  {factoriesData.map((f) => (
                    <button
                      type="button"
                      key={f.id}
                      className={`fo-factory-item ${f.id === selectedFactoryId ? 'active' : ''}`}
                      onClick={() => { setSelectedFactoryId(f.id); setIsDropdownOpen(false); }}
                    >
                      <span className="fo-factory-item-name">{f.factoryName}</span>
                      {f.address && <span className="fo-factory-item-sub">{f.address}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="fo-header-right" ref={refreshIndicatorRef}>
            <span className="fo-sync-time">{formatDateTime(syncTime)}</span>
            <button type="button" className="fo-refresh-btn" onClick={() => loadDashboard(true)} title={tf('refresh')}>
              <span className="material-symbols-outlined fo-refresh-icon">refresh</span>
            </button>
          </div>
        </div>

        {isInitialLoading ? (
          <div className="fo-message">{tf('loadingData')}</div>
        ) : (
        <>
        {/* ===== OVERVIEW ===== */}
        <section className="fo-panel fo-overview">
          <div className="fo-overview-head">
            <div className="fo-overview-title">
              <FactoryFlag country={factoryCountry} />
              <span className="fo-overview-name">{factoryName}</span>
              {factoryCode && <span className="fo-code-badge"><span className="fo-code-dot" />{factoryCode}</span>}
            </div>
            <span className="fo-overview-tag">{tf('overview')}</span>
          </div>
          <div className="fo-overview-body">
            <ArcGauge value={kpi.overallUtilization} label={tf('kpi.activeRatio')} />

            <div className="fo-kpis">
              <div className="fo-kpi">
                <div className="fo-kpi-label"><span className="material-symbols-outlined">precision_manufacturing</span>{tf('kpi.totalMachines')}</div>
                <div className="fo-kpi-value">{fmt(kpi.totalMachines)}<small>{tf('kpi.machineUnit')}</small></div>
              </div>
              <div className="fo-kpi">
                <div className="fo-kpi-label"><span className="material-symbols-outlined">play_arrow</span>{tf('kpi.running')}</div>
                <div className="fo-kpi-value">{fmt(kpi.runningMachines)}<small>{tf('kpi.machineUnit')}</small></div>
              </div>
              <div className="fo-kpi">
                <div className="fo-kpi-label"><span className="material-symbols-outlined">pause</span>{tf('kpi.idle')}</div>
                <div className="fo-kpi-value">{fmt(kpi.idleMachines)}<small>{tf('kpi.machineUnit')}</small></div>
              </div>
              <div className="fo-kpi">
                <div className="fo-kpi-label"><span className="material-symbols-outlined">stop</span>{tf('kpi.stopped')}</div>
                <div className="fo-kpi-value">{fmt(kpi.stoppedMachines)}<small>{tf('kpi.machineUnit')}</small></div>
              </div>
              <div className="fo-kpi">
                <div className="fo-kpi-label"><span className="material-symbols-outlined">notifications_active</span>{tf('kpi.pendingAlarms')}</div>
                <div className="fo-kpi-value">{fmt(alarmStatus.activeCount)}<small>{tf('kpi.caseUnit')}</small></div>
              </div>
              <div className="fo-kpi">
                <div className="fo-kpi-label"><span className="material-symbols-outlined">speed</span>{tf('kpi.oee')}</div>
                <div className="fo-kpi-value">{pct(kpi.oee)}<small>%</small></div>
              </div>
              <div className="fo-kpi fo-kpi-wide">
                <div className="fo-kpi-label"><span className="material-symbols-outlined">inventory_2</span>{tf('kpi.dailyProduction')}</div>
                <div className="fo-kpi-value">{fmt(kpi.dailyProduction)}<small>EA</small></div>
              </div>
            </div>

            <div className="fo-env">
              <span className="fo-env-title">{tf('environment.title')}</span>
              <div className="fo-env-item">
                <div className="fo-env-label"><span className="material-symbols-outlined">thermostat</span>{tf('environment.temperature')}</div>
                <div className="fo-env-value">{pct(currentFactory.temperature)}<small>{tf('environment.tempUnit')}</small></div>
              </div>
              <div className="fo-env-item">
                <div className="fo-env-label"><span className="material-symbols-outlined">humidity_percentage</span>{tf('environment.humidity')}</div>
                <div className="fo-env-value">{pct(currentFactory.humidity)}<small>%</small></div>
              </div>
              <div className="fo-env-item">
                <div className="fo-env-label"><span className="material-symbols-outlined">bolt</span>{tf('environment.energy')}</div>
                <div className="fo-env-value">{fmt(kpi.energyUsage)}<small>kWh</small></div>
              </div>
              <div className="fo-env-item">
                <div className="fo-env-label"><span className="material-symbols-outlined">graphic_eq</span>{tf('environment.noise')}</div>
                <div className="fo-env-value">{pct(kpi.noise)}<small>dB</small></div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== 라인별 현황 ===== */}
        <section className="fo-panel fo-lines">
          <h3 className="fo-panel-title">{tf('line.title')}</h3>
          {lineStatuses.length === 0 ? (
            <div className="fo-empty">{tf('line.empty')}</div>
          ) : (
            <div className="fo-line-grid" style={{ '--fo-line-cols': Math.min(lineStatuses.length, 3) }}>
              {lineStatuses.map((line) => {
                const total = line.machines || 0;
                const runPct = total ? (line.running / total) * 100 : 0;
                const idlePct = total ? (line.idle / total) * 100 : 0;
                const active = (line.running || 0) > 0;
                const alarmCount = Array.isArray(line.alarmTimes) ? line.alarmTimes.length : null;
                return (
                  <div
                    key={line.lineId}
                    className={`fo-line-card ${active ? 'is-active' : ''}`}
                    onClick={() => navigate(`/factory-machine-overview?factoryId=${selectedFactoryId}&lineId=${line.lineId}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/factory-machine-overview?factoryId=${selectedFactoryId}&lineId=${line.lineId}`); }}
                  >
                    <div className="fo-line-main">
                      <div className="fo-line-head">
                        <span className="fo-line-name">{line.lineName}</span>
                        <span className="fo-line-pct">{line.utilization == null ? '-' : `${Math.round(line.utilization)}%`}</span>
                      </div>
                      <div className="fo-line-bar">
                        <div className="fo-line-bar-run" style={{ width: `${runPct}%` }} />
                        <div className="fo-line-bar-idle" style={{ width: `${idlePct}%` }} />
                      </div>
                      <div className="fo-line-legend">
                        <span><i className="fo-dot fo-dot-run" />{tf('line.running')} {fmt(line.running)}</span>
                        <span><i className="fo-dot fo-dot-idle" />{tf('line.idle')} {fmt(line.idle)}</span>
                        <span><i className="fo-dot fo-dot-stop" />{tf('line.stopped')} {fmt(line.stopped)}</span>
                      </div>
                    </div>
                    <div className="fo-line-side">
                      <div className="fo-line-side-item">
                        <span className="fo-line-side-label">{tf('line.production')}</span>
                        <span className="fo-line-side-value">{active ? fmt(line.production) : '-'}<small>EA</small></span>
                      </div>
                      <div className="fo-line-side-item">
                        <span className="fo-line-side-label">{tf('line.alarms')}</span>
                        <span className="fo-line-side-value">{active && alarmCount != null ? alarmCount : '-'}<small>{tf('line.caseUnit')}</small></span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ===== 하단: 설비별 현황 + 실시간 알람 ===== */}
        <div className="fo-bottom">
          <section className="fo-panel fo-machines">
            <div className="fo-panel-head">
              <h3 className="fo-panel-title">{tf('machine.title')}</h3>
              <div className="fo-filters">
                <label className="fo-filter">
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">{tf('machine.statusFilter')}</option>
                    <option value="running">{tf('machine.running')}</option>
                    <option value="idle">{tf('machine.idle')}</option>
                    <option value="stopped">{tf('machine.stopped')}</option>
                  </select>
                  <span className="material-symbols-outlined">expand_more</span>
                </label>
                <label className="fo-filter">
                  <select value={lineFilter} onChange={(e) => handleLineFilter(e.target.value)}>
                    <option value="all">{tf('machine.lineFilter')}</option>
                    {lines.map((l) => <option key={l.id} value={l.id}>{l.lineName}</option>)}
                  </select>
                  <span className="material-symbols-outlined">expand_more</span>
                </label>
              </div>
            </div>

            <div className="fo-machine-scroll">
              {machinesLoading && machines.length === 0 ? (
                <div className="fo-empty">{tf('machine.loading')}</div>
              ) : visibleMachines.length === 0 ? (
                <div className="fo-empty">{tf('machine.empty')}</div>
              ) : (
                <div className="fo-machine-grid">
                  {visibleMachines.map((m, i) => {
                    const alarmed = (m.alarms || 0) > 0;
                    const running = m.status === 'running';
                    /* 빨간 표시 기준: 정지 상태이거나 진행 중인 위험 알람이 있을 때 */
                    const danger = m.status === 'stopped' || criticalMachineNames.has(m.name);
                    return (
                      <div
                        key={m.id}
                        className={`fo-machine-card fo-machine-${m.status || 'stopped'} ${alarmed ? 'is-alarmed' : ''} ${danger ? 'is-danger' : ''}`}
                        onClick={() => goMachine(m)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') goMachine(m); }}
                        title={m.name}
                      >
                        <div className="fo-machine-top">
                          <span className="fo-machine-no">{machineNumber(m, i)}</span>
                          <div className="fo-machine-icons">
                            {alarmed && (
                              <button
                                type="button"
                                className="fo-alarm-badge"
                                onClick={(e) => { e.stopPropagation(); goMachine(m, 'alarm'); }}
                                title={tf('machine.alarmsTitle', { n: m.alarms })}
                              >
                                {m.alarms}
                              </button>
                            )}
                            <span className={`fo-machine-state-icon ${running ? 'run' : 'stop'}`}>
                              <span className="material-symbols-outlined">{running ? 'play_arrow' : 'stop'}</span>
                            </span>
                          </div>
                        </div>
                        <div className="fo-machine-row">
                          <span className="fo-machine-key">{tf('machine.moldNumber')}</span>
                          <span className="fo-machine-val">{m.craftId || m.productName || '-'}</span>
                        </div>
                        <div className="fo-machine-row">
                          <span className="fo-machine-key">{tf('machine.shotCount')}</span>
                          <span className="fo-machine-val">{fmt(m.shotNo)}</span>
                        </div>
                        {/* CAV·C/T: 같은 응답의 cavityCount(최근 샷 mold_cavity, 없으면 설비 등록값) / cycleTime(초, 최근 샷). 0 이하·없음은 — */}
                        <div className="fo-machine-row fo-machine-spec">
                          <span className="fo-machine-spec-item">
                            <span className="fo-machine-spec-key">{tf('machine.cav')}</span>
                            <span className="fo-machine-spec-val">{positive(m.cavityCount) ? Number(m.cavityCount) : '—'}</span>
                          </span>
                          <span className="fo-machine-spec-item">
                            <span className="fo-machine-spec-key">{tf('machine.ct')}</span>
                            <span className="fo-machine-spec-val">{positive(m.cycleTime) ? `${Number(m.cycleTime).toFixed(1)}s` : '—'}</span>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <section className="fo-panel fo-alarms">
            <div className="fo-panel-head">
              <h3 className="fo-panel-title">{tf('alarm.title')}</h3>
              <div className="fo-alarm-filters">
                {['all', 'critical', 'warning'].map((key) => (
                  <button
                    type="button"
                    key={key}
                    className={`fo-alarm-chip fo-alarm-chip-${key} ${alarmFilter === key ? 'active' : ''}`}
                    onClick={() => setAlarmFilter(key)}
                  >
                    {tf(`alarm.${key}`)} <b>{alarmCounts[key]}</b>
                  </button>
                ))}
              </div>
            </div>

            <div className="fo-alarm-scroll">
              {visibleAlarms.length === 0 ? (
                <div className="fo-empty">
                  <span className="material-symbols-outlined">check_circle</span>
                  {tf('alarm.noActiveAlarms')}
                </div>
              ) : (
                visibleAlarms.map((alarm, index) => {
                  const sev = normalizeSeverity(alarm.severity);
                  const equipmentId = alarm.equipmentId || alarm.machineId || alarm.equipment_id || alarm.machine_id;
                  const machineName = alarm.machine || alarm.equipmentName || alarm.machineName;
                  const machineData = allMachinesMap[equipmentId] || { id: equipmentId, name: machineName };
                  return (
                    <div
                      key={`${equipmentId}-${alarm.alarmId}-${index}`}
                      className={`fo-alarm-card fo-alarm-${sev}`}
                      onClick={() => navigate('/machine', { state: { machineData, openTab: 'alarm', alarmId: alarm.alarmId } })}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') navigate('/machine', { state: { machineData, openTab: 'alarm', alarmId: alarm.alarmId } }); }}
                    >
                      <div className="fo-alarm-head">
                        <span className="fo-alarm-machine">{machineName || '-'}</span>
                        <span className="fo-alarm-meta">{alarm.time || '-'}{alarm.duration ? ` · ${alarm.duration}` : ''}</span>
                      </div>
                      <div className="fo-alarm-message">{alarm.message}</div>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </div>
        </>
        )}
      </div>
    </MainLayout>
  );
}

export default Factory;
